<script setup>
import {ref, defineExpose } from 'vue'
import KSAVideo from "@/components/ui-components/video/KSAVideo.vue";

const corp = ref({});

const setCorpInfo = (value) => {
  corp.value = value;
  // console.log('body', corp);
}

defineExpose({
  setCorpInfo,
})

const getFullUrl = (url) => {
  if (!url) return '#' // url이 없으면 기본 값
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return 'https://' + url
  }
  return url
}

const getYoutubeEmbedUrl = (url) => {
  if (!url) return ''

  const fullUrl = getFullUrl(url)

  try {
    const parsedUrl = new URL(fullUrl)
    const videoId = parsedUrl.searchParams.get('v')

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`
    }

    if (parsedUrl.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${parsedUrl.pathname.replace('/', '')}`
    }

    if (parsedUrl.pathname.includes('/embed/')) {
      return fullUrl
    }
  } catch (error) {
    return ''
  }

  return ''
}
</script>

<template>
  <!-- s : 기업 정보 조회 -->
  <div class="resume-wrap">
    <div class="self-info">
      <div class="img">
        <KSAImage :src="corp.ciImgDir" />
      </div>
      <div class="text">
        <h1>{{ corp.corpName }}</h1>
        <p>{{ $gbNameKR('INDUSTRY_GB', corp.industryGb) }}/ {{ corp.bizType }}</p>
      </div>
    </div>

    <section v-if="corp.introMovDir || corp.introYoutubeUrl" class="intro-video">
      <div v-if="corp.introMovGb === '02' && getYoutubeEmbedUrl(corp.introYoutubeUrl)" class="video-frame">
        <iframe
          :src="getYoutubeEmbedUrl(corp.introYoutubeUrl)"
          title="기업 소개 영상"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        />
      </div>
      <div v-else-if="corp.introMovDir" class="video-frame">
        <video controls preload="metadata">
          <source :src="corp.introMovDir" />
          브라우저가 동영상 재생을 지원하지 않습니다.
        </video>
      </div>
    </section>

    <!-- s : 기업 기본 정보 -->
    <section class="basic">
      <h2>기업 기본 정보</h2>
      <div class="list-wrapper">
        <dl>
          <dt>기업형태</dt>
          <dd>{{ $gbNameKR('CORP_GB', corp.corpGb)}}</dd>
        </dl>
        <dl>
          <dt>창립연월</dt>
          <dd>{{ $yyyymm(corp.foundYm) }}</dd>
        </dl>
        <dl>
          <dt>산업군</dt>
          <dd>{{ $gbNameKR('INDUSTRY_GB', corp.industryGb) }}</dd>
        </dl>
        <dl>
          <dt>사원수</dt>
          <dd>{{ $gbNameKR('EMPLOYEE_NUM_GB', corp.employeeNumGb) }}</dd>
        </dl>
        <dl>
          <dt>업종</dt>
          <dd>{{ corp.bizType }}</dd>
        </dl>
        <dl>
          <dt>매출액</dt>
          <dd>{{ $gbNameKR('SALES_AMT_GB', corp.salesAmtGb) }}</dd>
        </dl>
        <dl>
          <dt>대표자명</dt>
          <dd>{{ corp.ceoName }}</dd>
        </dl>
        <dl>
          <dt>상장여부</dt>
          <dd>
            <span v-if="corp.corpPublicYn === 'Y'">
              상장
              {{ $gbNameKR('CORP_PUBLIC_GB', corp.corpPublicGb) }}
            </span>
            <span v-else>
              비상장
            </span>
          </dd>
        </dl>
        <dl>
          <dt>사업장소재지</dt>
          <dd>{{ corp.residenceArea1 }} {{ corp.residenceArea2 }}</dd>
        </dl>
        <dl>
          <dt>홈페이지</dt>
          <dd><a :href="getFullUrl(corp.homeUrl)" target="_blank" rel="noopener noreferrer" class="link">{{ corp.homeUrl }}</a></dd>
        </dl>
      </div>
    </section>
    <!-- e : 기업 기본 정보 -->

    <!-- s : 기업 소개 -->
    <section class="board-detail" v-if="corp.corpIntro">
      <div class="word-break-all body">
        <h2>기업 소개</h2>
        <p class="text" v-html="corp.corpIntro"></p>
      </div>
    </section>
    <!-- e : 기업 소개 -->

    <!-- s : 주요 연혁 -->
    <section class="board-detail" v-if="corp.corpHistory">
      <div class="word-break-all body">
        <h2>주요 연혁</h2>
        <p class="text" v-html="corp.corpHistory"></p>
      </div>
    </section>
    <!-- e : 주요 연혁 -->

    <!-- s : 기업 소개서 -->
    <section class="army" v-if="corp.corpIntroName">
      <dl>
        <dt><h2>기업 소개서</h2></dt>
        <dd>
          <p class="file" @click="$downloadForPrivate({fileName:corp.corpIntroName, fileDir: corp.corpIntroDir})">
            {{ corp.corpIntroName }}
            <span v-if="corp.corpIntroSize">({{ $fileSize(corp.corpIntroSize) }})</span>
          </p>
        </dd>
      </dl>
    </section>
    <!-- e : 기업 소개서 -->
  </div>
  <!-- e : 기업 정보 조회 -->
</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";

.resume-wrap {
  background-color: $gray-thin_color;
  padding: 30px;

  .file, .link {
    color: $blue_color !important;
    cursor: pointer;
  }
  .link {
    color: $blue_color;
    text-decoration: underline;
  }
  .board-detail {
    * {
      width: 100%;
    }
    .body {
      border-bottom: 0;
      .text {
        padding: 0;
      }
    }
  }
  .self-info {
    display: flex;
    align-items: center;
    border-bottom: 2px solid $black_color;
    padding-bottom: 20px;
    .img {
      border: 1px solid $gray-light_color;
      width: 200px;
      height: 80px;
      margin-right: 20px;
      img {
        object-fit: contain;
      }
    }
    h1 {
      @include font-size_md;
    }
    p {
      @include font-size_sm;
      word-break: normal;
    }
  }
  section {
    border-bottom: 1px solid $black_color;
    padding: 30px 0;
    &:last-child {
      border-bottom: 0;
      padding: 30px 0;
    }
    &.basic {
      .list-wrapper {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: flex-start;
        dl {
          flex-basis: 50%;
          dd {
            word-break: normal;
          }
        }
      }
    }
    &.army {
      h2 {
        color: $black_color;
      }
    }
    &.intro-video {
      .video-frame {
        width: 100%;
        max-width: 800px;
        aspect-ratio: 16 / 9;
        background: #000;
        overflow: hidden;
        margin: 0 auto;
      }

      iframe,
      video {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
      }
    }
    h2 {
      @include font-size_md;
      margin-bottom: 20px;
    }
    dl {
      display: flex;
      margin-bottom: 30px;
      &:last-child {
        margin-bottom: 0;
      }
      dt {
        width: 192px;
        color: $gray_color;
        b {
          display: block;
          color: $black_color;
          margin-bottom: 6px;
        }
      }
      dd {
        width: calc(100% - 192px);
        b {
          display: block;
          margin-bottom: 6px;
        }
        p {
          span {
            position: relative;
            padding-left: 10px;
            margin-left: 10px;
            &:before {
              content: "";
              position: absolute;
              top: 50%;
              left: 0;
              width: 1px;
              height: 10px;
              background: $gray-regular_color;
              -webkit-transform: translateY(-50%);
              transform: translateY(-50%);
            }
          }
        }
      }
    }
  }
}
</style>
